import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  IonContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import { useParams } from "react-router-dom";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useVideoContextWindow } from "../../hooks/useVideoContextWindow";
import { VideoSwiper } from "../../components/atoms/VideoSwiper";
import { VideoSlide } from "../../components/atoms/VideoSlide";
import { VideoMeta } from "../../services/video.service";
import { useTranslation } from "react-i18next";

const SOUND_STORAGE_KEY = "mentalplus-video-muted";

const PageWrapper = styled.div`
  position: relative;
  height: 100vh;
  height: 100dvh;
  background: #000;
`;

const TopBar = styled.div`
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 20;
`;

const TopButton = styled.button`
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  font-size: 16px;
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
`;

const VideoInfoOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 80px 16px 40px;
  display: flex;
  align-items: flex-end;
  pointer-events: none;
`;

const VideoInfoCard = styled.div`
  max-width: 78%;
  color: white;
  pointer-events: auto;
`;

const VideoDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  cursor: pointer;
`;

const MoreText = styled.span`
  font-weight: 700;
  margin-left: 6px;
  color: #ffffff;
`;

const shareUrlFrom = (hlsUrl: string) => {
  try {
    const u = new URL(hlsUrl);
    const parts = u.pathname.split("/").filter(Boolean);
    const uid = parts[0];
    return `${u.origin}/${uid}/watch`;
  } catch {
    return hlsUrl;
  }
};

const truncateText = (text: string, maxLength = 110) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
};

const hasExplicationTarget = (vid?: VideoMeta) => {
  const linkedUid = vid?.meta?.linked_uid?.trim();
  const linkedUrl = vid?.meta?.linked_url?.trim();
  return Boolean(linkedUid || linkedUrl);
};

const fullscreenContentStyle = {
  "--offset-top": "0px",
  "--padding-top": "0px",
  "--padding-bottom": "0px",
  "--background": "#000",
} as React.CSSProperties;

const DisplayVideo: React.FC = () => {
  const [pageActive, setPageActive] = useState(false);
  const [liked, setLiked] = useState(false);
  const [shared, setShared] = useState(false);
  const [savedVideoIds, setSavedVideoIds] = useState<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { id } = useParams<{ id: string }>();
  const ionRouter = useIonRouter();
  const { setResonanceLayout } = useResonanceLayout();
  const { t } = useTranslation();
  const wantPlayRef = useRef(false);

  const urlSearch = window.location.search;

  const searchParams = useMemo(() => {
    return new URLSearchParams(urlSearch);
  }, [urlSearch]);

  const tags = useMemo(() => {
    return (
      searchParams
        .get("tags")
        ?.split(",")
        .map((t) => t.trim())
        .filter(Boolean) ?? []
    );
  }, [searchParams]);

  const searchQuery = useMemo(() => {
    return searchParams.get("search")?.trim() ?? "";
  }, [searchParams]);

  const {
    videos,
    currentIndex,
    setCurrentIndex,
    loadPrev,
    loadNext,
    initialSlide,
    swiperKey,
    initSwiperDoneRef,
    suppressEdgeLoadsRef,
  } = useVideoContextWindow(id, tags, searchQuery);

  const activeVideo = videos[currentIndex];
  const description = activeVideo?.meta?.description || "";

  useEffect(() => {
    setExpanded(false);
  }, [currentIndex]);

  useEffect(() => {
    const savedMuted = localStorage.getItem(SOUND_STORAGE_KEY);

    if (savedMuted === null) {
      setMuted(false);
    } else {
      setMuted(savedMuted === "true");
    }
  }, []);

  useEffect(() => {
  const syncSavedVideos = () => {
    const raw = localStorage.getItem("savedVideos");

    if (!raw) {
      setSavedVideoIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed)
        ? parsed.map((v: any) => (typeof v === "string" ? v : v.uid))
        : [];
      setSavedVideoIds(ids);
    } catch {
      setSavedVideoIds([]);
    }
  };

  syncSavedVideos();

  window.addEventListener("saved-videos-updated", syncSavedVideos);

  return () => {
    window.removeEventListener("saved-videos-updated", syncSavedVideos);
  };
}, []);
  const handleToggleMute = () => {
    setMuted((prev) => {
      const next = !prev;
      localStorage.setItem(SOUND_STORAGE_KEY, String(next));
      return next;
    });
  };

  const handleToggleSave = (vid: any) => {
  const raw = localStorage.getItem("savedVideos");
  const current = raw ? JSON.parse(raw) : [];

  const exists = current.some((v: any) => v.uid === vid.uid);

  const next = exists
    ? current.filter((v: any) => v.uid !== vid.uid)
    : [
        ...current,
        {
          uid: vid.uid,
          hlsUrl: vid.hlsUrl,
          thumbnailUrl: vid.thumbnailUrl,
          meta: vid.meta,
        },
      ];

  localStorage.setItem("savedVideos", JSON.stringify(next));
  setSavedVideoIds(next.map((v: any) => v.uid));

  window.dispatchEvent(new Event("saved-videos-updated"));
};
  const handleShowExplication = (vid: VideoMeta) => {
    const linkedUid = vid.meta?.linked_uid?.trim();
    const linkedUrl = vid.meta?.linked_url?.trim();

    if (linkedUid) {
      const url = new URL(window.location.href);
      const parts = url.pathname.split("/").filter(Boolean);

      if (parts.length >= 2) {
        parts[parts.length - 2] = linkedUid;
      }

      const newPath = "/" + parts.join("/");
      const newUrl = newPath + url.search;

      ionRouter.push(newUrl, "forward", "push");
      return;
    }

    if (linkedUrl) {
      window.open(linkedUrl, "_blank", "noopener,noreferrer");
    }
  };

  useIonViewDidEnter(() => {
    setPageActive(true);
    wantPlayRef.current = true;
  });

  useEffect(() => {
    const tabBar = document.getElementById("ion-tab-bar");

    if (tabBar) {
      tabBar.style.display = "none";
    }

    return () => {
      if (tabBar) {
        tabBar.style.display = "flex";
      }
    };
  }, []);

  useIonViewDidLeave(() => {
    setPageActive(false);
    wantPlayRef.current = false;
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: t("mentalplus_tab_videos"),
    });
  });

  return (
    <IonPage>
      <IonContent scrollY={false} fullscreen style={fullscreenContentStyle}>
        <PageWrapper>
          <TopBar>
            <TopButton onClick={() => history.back()}>
              ← {t("mentalplus_video_retour")}
            </TopButton>
          </TopBar>

          <VideoSwiper<VideoMeta>
            items={videos}
            index={currentIndex}
            setIndex={(i) => {
              setCurrentIndex(i);

              const nextId = videos[i]?.uid;
              if (nextId && id && nextId !== id) {
                const url = new URL(window.location.href);
                const parts = url.pathname.split("/").filter(Boolean);

                if (parts.length >= 2) {
                  parts[parts.length - 2] = nextId;
                }

                const newPath = "/" + parts.join("/");
                const newUrl = newPath + url.search;
                window.history.replaceState(null, "", newUrl);
              }
            }}
            initialSlide={initialSlide}
            remountKey={swiperKey}
            onNearStart={loadPrev}
            onNearEnd={loadNext}
            initDoneRef={initSwiperDoneRef}
            suppressEdgeLoadsRef={suppressEdgeLoadsRef}
            renderItem={(vid, i) => (
              <VideoSlide
                src={vid.hlsUrl}
                thumbnail={vid.thumbnailUrl}
                active={pageActive && i === currentIndex}
                shouldPlay={pageActive && i === currentIndex}
                muted={muted}
                liked={liked}
                shared={shared}
                saved={savedVideoIds.includes(vid.uid)}
                showExplication={hasExplicationTarget(vid)}
                onToggleLike={() => setLiked((v) => !v)}
                onToggleSave={() => handleToggleSave(vid)}
                onShare={async () => {
                  const url = shareUrlFrom(vid.hlsUrl);
                  await navigator.clipboard.writeText(url);
                  setShared(true);
                  setTimeout(() => setShared(false), 1200);
                }}
                onToggleMute={handleToggleMute}
                onShowExplication={() => handleShowExplication(vid)}
              />
            )}
          />

          {activeVideo && (
            <VideoInfoOverlay>
              <VideoInfoCard>
                {description && (
                  <VideoDescription
                    onClick={() => setExpanded((prev) => !prev)}
                  >
                    {expanded ? description : truncateText(description, 30)}

                    {description.length > 110 && (
                      <MoreText>
                        {expanded ? "voir moins" : "voir plus"}
                      </MoreText>
                    )}
                  </VideoDescription>
                )}
              </VideoInfoCard>
            </VideoInfoOverlay>
          )}
        </PageWrapper>
      </IonContent>
    </IonPage>
  );
};

export default DisplayVideo;